import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../lib/api.js';
import { type TokenClaims } from '../lib/auth.js';
import { getStoredActiveOrgId, setStoredActiveOrgId } from '../lib/active-org.js';
import { useAuth } from './auth.js';

export interface OrgMembership {
  orgId: string;
  orgName: string;
  role: string;
  joinedAt?: string;
}

interface OrgContextValue {
  memberships: OrgMembership[];
  managedOrgs: OrgMembership[];
  selectedOrgId: string | null;
  selectedOrg: OrgMembership | null;
  loading: boolean;
  refresh: () => Promise<void>;
  setSelectedOrgId: (orgId: string | null) => void;
}

const OrgContext = createContext<OrgContextValue | null>(null);

function normalizeMemberships(memberships: OrgMembership[], claims: TokenClaims | null): OrgMembership[] {
  const deduped = new Map<string, OrgMembership>();

  for (const membership of memberships) {
    if (!membership.orgId) {
      continue;
    }

    deduped.set(membership.orgId, membership);
  }

  if (claims?.org_id) {
    deduped.set(claims.org_id, {
      orgId: claims.org_id,
      orgName: claims.org_name ?? claims.org_id,
      role: claims.org_role ?? 'member',
      joinedAt: undefined,
    });
  }

  return Array.from(deduped.values());
}

function chooseSelectedOrgId(
  memberships: OrgMembership[],
  userId: string,
  claims: TokenClaims | null,
): string | null {
  if (memberships.length === 0) {
    return null;
  }

  const membershipIds = new Set(memberships.map((membership) => membership.orgId));
  const candidateIds = [
    getStoredActiveOrgId(userId),
    claims?.org_id ?? null,
    memberships[0]?.orgId ?? null,
  ];

  for (const candidate of candidateIds) {
    if (candidate && membershipIds.has(candidate)) {
      return candidate;
    }
  }

  return memberships[0]?.orgId ?? null;
}

export function OrgProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { authenticated, claims, loading: authLoading } = useAuth();
  const [memberships, setMemberships] = useState<OrgMembership[]>([]);
  const [selectedOrgIdState, setSelectedOrgIdState] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const applySelectedOrgId = useCallback((orgId: string | null) => {
    setSelectedOrgIdState(orgId);
    if (claims?.sub) {
      setStoredActiveOrgId(claims.sub, orgId);
    }
  }, [claims?.sub]);

  const refresh = useCallback(async () => {
    if (!authenticated || !claims?.sub) {
      setMemberships([]);
      setSelectedOrgIdState(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const result = await api.get<{ orgs?: OrgMembership[] }>('/api/v1/orgs/my-orgs');
      const normalizedMemberships = normalizeMemberships(result.orgs ?? [], claims);
      const nextSelectedOrgId = chooseSelectedOrgId(normalizedMemberships, claims.sub, claims);

      setMemberships(normalizedMemberships);
      applySelectedOrgId(nextSelectedOrgId);
    } catch {
      const fallbackMemberships = normalizeMemberships([], claims);
      const nextSelectedOrgId = chooseSelectedOrgId(fallbackMemberships, claims.sub, claims);

      setMemberships(fallbackMemberships);
      applySelectedOrgId(nextSelectedOrgId);
    } finally {
      setLoading(false);
    }
  }, [applySelectedOrgId, authenticated, claims]);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    void refresh();
  }, [authLoading, refresh]);

  useEffect(() => {
    if (loading || memberships.length === 0) {
      return;
    }

    const orgRouteMatch = location.pathname.match(/^\/org\/([^/]+)/);
    if (!orgRouteMatch) {
      return;
    }

    const routeOrgId = decodeURIComponent(orgRouteMatch[1] ?? '');
    if (!routeOrgId || routeOrgId === 'me') {
      return;
    }

    if (!memberships.some((membership) => membership.orgId === routeOrgId)) {
      return;
    }

    if (routeOrgId !== selectedOrgIdState) {
      applySelectedOrgId(routeOrgId);
    }
  }, [applySelectedOrgId, loading, location.pathname, memberships, selectedOrgIdState]);

  const managedOrgs = useMemo(
    () => memberships.filter((membership) => membership.role === 'owner' || membership.role === 'admin'),
    [memberships],
  );

  const selectedOrg = useMemo(
    () => memberships.find((membership) => membership.orgId === selectedOrgIdState) ?? null,
    [memberships, selectedOrgIdState],
  );

  const setSelectedOrgId = useCallback((orgId: string | null) => {
    if (!orgId) {
      applySelectedOrgId(null);
      return;
    }

    if (!memberships.some((membership) => membership.orgId === orgId)) {
      return;
    }

    applySelectedOrgId(orgId);
  }, [applySelectedOrgId, memberships]);

  const value = useMemo<OrgContextValue>(() => ({
    memberships,
    managedOrgs,
    selectedOrgId: selectedOrgIdState,
    selectedOrg,
    loading,
    refresh,
    setSelectedOrgId,
  }), [loading, managedOrgs, memberships, refresh, selectedOrg, selectedOrgIdState, setSelectedOrgId]);

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useOrgContext(): OrgContextValue {
  const context = useContext(OrgContext);
  if (!context) {
    throw new Error('useOrgContext must be used within an OrgProvider');
  }

  return context;
}